import Config from '@config';
import {
  EmployeeByRoleGridify,
  EmployeeGridify,
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  infiniteQueryOptionsFn,
  normalizeInfiniteQueryData,
} from '@gasbi/shared';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  Employee,
  EmployeeByEmail,
  EmployeeRequestPayload,
  EmployeeResponse,
} from './types';

export type EmployeeDeactivationPayload = {
  employeeId: string;
  endDate: string;
};

export const employeesApi = createApi({
  reducerPath: 'employeesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/employees',
  }),
  tagTypes: ['Employees', 'Employee', 'EmployeesByRole'],
  endpoints: builder => ({
    getEmployees: builder.infiniteQuery<
      Employee[],
      FiltersGrid & { roleIds?: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        const { roleIds, ...rest } = queryArg;
        return {
          url: roleIds ? `/query?${roleIds}` : '/query',
          method: 'GET',
          params: { ...rest, ...pageParam },
        };
      },
      providesTags: () => ['Employees'],
    }),
    getEmployeeById: builder.query<Employee, string>({
      query: id => ({ url: `/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Employee', id }],
    }),
    getEmployeeByEmail: builder.query<EmployeeByEmail, { email: string }>({
      query: params => ({ url: '/by-email', method: 'GET', params }),
    }),
    createEmployee: builder.mutation<EmployeeResponse, EmployeeRequestPayload>({
      query: data => ({
        url: '',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Employees'],
    }),
    updateEmployee: builder.mutation<EmployeeResponse, EmployeeRequestPayload>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Employees',
        { type: 'Employee', id },
      ],
    }),
    sendActivationRequest: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}/send-activation-request`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Employees',
        { type: 'Employee', id },
      ],
    }),
    sendActivationRequestByIdentity: builder.mutation<void, string>({
      query: identityId => ({
        url: '/send-activation-request-by-identity',
        method: 'POST',
        params: { identityId },
      }),
      invalidatesTags: ['Employees'],
    }),
    removePassword: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}/remove-password`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Employees',
        { type: 'Employee', id },
      ],
    }),
    removePasswordByIdentity: builder.mutation<void, string>({
      query: identityId => ({
        url: '/remove-password-by-identity',
        method: 'POST',
        params: { identityId },
      }),
      invalidatesTags: ['Employees'],
    }),
    deactivateEmployee: builder.mutation<void, EmployeeDeactivationPayload>({
      query: ({ employeeId, endDate }) => ({
        url: `/${employeeId}/deactivate`,
        method: 'POST',
        data: { employeeId, endDate },
      }),
      invalidatesTags: (_result, _error, { employeeId }) => [
        'Employees',
        { type: 'Employee', id: employeeId },
      ],
    }),
    activateEmployee: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Employees',
        { type: 'Employee', id },
      ],
    }),
    deactivateEmployeeByIdentity: builder.mutation<void, string>({
      query: identityId => ({
        url: '/deactivate-by-identity',
        method: 'POST',
        params: { identityId },
      }),
      invalidatesTags: ['Employees'],
    }),
    getEmployeesByRoleI: builder.infiniteQuery<
      EmployeeByRoleGridify,
      FiltersGrid & {
        organizationId: string;
        roleId?: string;
        name?: string;
      },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/by-role`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['EmployeesByRole'],
    }),
    getEmployeesByRole: builder.query<
      EmployeeByRoleGridify[],
      FiltersGrid & { organizationId: string; roleId?: string; name?: string }
    >({
      query: params => ({
        url: `/by-role`,
        params,
      }),
      providesTags: () => ['EmployeesByRole'],
    }),

    getEmployeesAccessibleByOrganization: builder.infiniteQuery<
      EmployeeGridify,
      FiltersGrid & { organizationId: string; name?: string },
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 500 }),
      query: ({ queryArg, pageParam }) => ({
        url: `/accessible/for-organization`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Employees'],
    }),
  }),
});

export const useGetEmployeesInfiniteQuery =
  normalizeInfiniteQueryData<Employee>(
    employeesApi.useGetEmployeesInfiniteQuery,
  );

export const useGetEmployeesByRoleInfiniteQuery = normalizeInfiniteQueryData(
  employeesApi.useGetEmployeesByRoleIInfiniteQuery,
);

export const useGetEmployeesAccessibleByOrganizationQuery =
  normalizeInfiniteQueryData(
    employeesApi.useGetEmployeesAccessibleByOrganizationInfiniteQuery,
  );

export const {
  useGetEmployeeByIdQuery,
  useLazyGetEmployeeByEmailQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useSendActivationRequestMutation,
  useSendActivationRequestByIdentityMutation,
  useRemovePasswordMutation,
  useRemovePasswordByIdentityMutation,
  useDeactivateEmployeeMutation,
  useDeactivateEmployeeByIdentityMutation,
  useActivateEmployeeMutation,

  useGetEmployeesByRoleQuery,
} = employeesApi;
